import React from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faTrash } from '@fortawesome/pro-regular-svg-icons';

import type { CallListItem } from 'bundles/connect/types';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import { deleteCallListItem } from 'bundles/connect/helpers/prospect_api';
import { setOffMarketPropertyId } from 'bundles/connect/components/store/actions/off_market_property';
import { setCallListItemId } from '../store/actions/call_console';
import { faLock } from '@fortawesome/pro-solid-svg-icons';
import { setCallListItems, setPage, setTotalPages } from '../store/actions/results';
import { selectCallListItems, selectPage, selectTotalPages } from '../store/selectors/results';

type CallListItemTableRowProps = {
  callListItem: CallListItem;
  callListId: number;
  lastUpdated?: string;
  setOffMarketPropertyId: (id: string) => void;
}

function CallListItemTableRow({ callListItem,
  callListId,
  lastUpdated,
  setOffMarketPropertyId }: CallListItemTableRowProps) {
  const dispatch = useDispatch();
  const callListItems = useSelector(selectCallListItems);
  const totalPages = useSelector(selectTotalPages);
  const page = useSelector(selectPage);

  const handlePropertyClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(setCallListItemId(callListItem.id));
    setOffMarketPropertyId(String(callListItem.insightsPropertyId));
  };

  const handleRemoveFromList = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (confirm('Are you sure?')) {
      deleteCallListItem(callListItem.id).then(() => {
        const newItems = callListItems.filter(item => item.id !== callListItem.id);
        dispatch(setCallListItems(newItems));
        if (newItems.length === 0 && page > 1) {
          dispatch(setPage(page - 1));
          dispatch(setTotalPages(totalPages - 1));
        }
        displayToast('Item successfully removed from list', 'success');
      });
    }
  };

  return (
    <tr id={`call_list_item_${callListItem.id}`}>
      {/* Mobile row */}
      <td className="d-lg-none py-3 px-4">
        <a href={`/call_console/connect/connect_call_list/${callListId}?item_id=${callListItem.id}`}>
          {callListItem.callListable.name}
        </a>
      </td>
      {/* Property */}
      <td className="d-none d-lg-table-cell sticky-cell py-3 px-4 text-truncate align-middle">
        {
          callListItem.insightsPropertyId && (
            <Button
              as="a"
              bsPrefix="btn-link"
              className="p-0 fw-normal"
              onClick={handlePropertyClick}
            >
              {callListItem.callListable.name}
            </Button>
          )}
      </td>
      {/* Contact */}
      <td className="d-none d-xl-table-cell align-middle text-truncate py-3 px-4">
        {callListItem.restricted ? (
          <>
            <i className="fa fas fa-lock me-2" />
            Locked
          </>
        ) : callListItem.numContacts >= 0 ? (
          <span>{callListItem.numContacts} Contacts</span>
        ) : (
          <span>
            <FontAwesomeIcon className="me-2" icon={faLock} />
            Unlock Contacts
          </span>
        )}
      </td>
      {/* Contacted */}
      <td className="d-none d-lg-table-cell py-3 px-4">
        <p className="m-0">
          {lastUpdated
            ? new Date(lastUpdated).toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
              hour12: true,
            }).replace(',', ' at')
            : '--'}
        </p>
      </td>
      {/* Added */}
      <td className="d-none d-lg-table-cell py-3 px-4">
        <p className="m-0">
          {new Date(callListItem.createdAt).toLocaleString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          }).replace(',', ' at')}
        </p>
      </td>
      {/* Actions */}
      <td className="d-none d-lg-table-cell py-3 px-4">
        <div className="d-flex align-items-center gap-2">
          <OverlayTrigger
            overlay={<Tooltip>Go to Console</Tooltip>}
            placement="left"
          >
            <a
              className="squared btn btn-text-primary p-1"
              href={`/prospect/connect_call_lists/${callListId}/call_console?item_id=${callListItem.id}`}
            >
              <FontAwesomeIcon icon={faBolt} />
            </a>
          </OverlayTrigger>
          <OverlayTrigger
            overlay={<Tooltip>Remove from List</Tooltip>}
            placement="left"
          >
            <Button
              as="a"
              className="squared p-1 text-danger"
              variant="ghost"
              onClick={handleRemoveFromList}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </OverlayTrigger>
        </div>
      </td>
    </tr>
  );
}

export default connect(undefined, { setOffMarketPropertyId })(CallListItemTableRow);
